import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Calendar, 
  Clock, 
  Phone, 
  Video, 
  MapPin, 
  ArrowLeft, 
  CheckCircle, 
  Loader2,
  User,
  Mail,
  PhoneCall
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Check if current time is within office hours (Mountain Time)
const isOfficeHours = () => {
  const now = new Date();
  const options = { timeZone: 'America/Denver', hour: 'numeric', minute: 'numeric', hour12: false };
  const mtTime = new Intl.DateTimeFormat('en-US', options).format(now);
  const [hour] = mtTime.split(':').map(Number);
  
  const dayOptions = { timeZone: 'America/Denver', weekday: 'short' };
  const dayOfWeek = new Intl.DateTimeFormat('en-US', dayOptions).format(now);
  
  // Office closed on weekends
  if (dayOfWeek === 'Sat' || dayOfWeek === 'Sun') {
    return false;
  }
  
  // Friday: 9 AM - 4 PM
  if (dayOfWeek === 'Fri') {
    return hour >= 9 && hour < 16;
  }
  
  // Monday-Thursday: 9 AM - 5 PM
  return hour >= 9 && hour < 17;
};

const ScheduleMeeting = () => {
  const [step, setStep] = useState(1); // 1: Loading/Type, 2: Select Time, 3: Details, 4: Confirmation
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [agentInfo, setAgentInfo] = useState(null);
  const [submissionId, setSubmissionId] = useState('');
  
  // Meeting selection
  const [meetingType, setMeetingType] = useState(''); // 'phone', 'video', 'in-person'
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Customer details
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  
  // Confirmation data
  const [confirmationData, setConfirmationData] = useState(null);
  
  // Call agent modal
  const [showCallModal, setShowCallModal] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callingAgent, setCallingAgent] = useState(false);

  const handleCallAgent = () => {
    if (!agentInfo?.phone) {
      toast.error('Agent phone number not available');
      return;
    }
    setShowCallModal(true);
  };

  const initiateCallToAgent = async () => {
    if (!callbackPhone) {
      toast.error('Please enter your phone number');
      return;
    }

    setCallingAgent(true);
    try {
      const agentPhone = agentInfo.phone.replace(/\D/g, '');
      
      const response = await fetch(`${BACKEND_URL}/api/click-to-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_phone: callbackPhone,
          target_phone: agentPhone.length === 10 ? `+1${agentPhone}` : `+${agentPhone}`,
          submission_id: submissionId
        })
      });

      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success(`Calling you now! You'll be connected to ${agentInfo.name}`);
        setShowCallModal(false);
        setCallbackPhone('');
      } else {
        toast.error(data.message || 'Failed to initiate call');
      }
    } catch (err) {
      console.error('Call error:', err);
      toast.error('Failed to initiate call. Please try again.');
    } finally {
      setCallingAgent(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const storedSubmissionId = localStorage.getItem('submissionId');
      if (storedSubmissionId) {
        setSubmissionId(storedSubmissionId);
        
        // Try to get stored form data for pre-filling
        const formDataKey = Object.keys(localStorage).find(key => key.startsWith('formData_'));
        if (formDataKey) {
          try {
            const formData = JSON.parse(localStorage.getItem(formDataKey));
            setCustomerInfo(prev => ({
              ...prev,
              name: `${formData.firstName || ''} ${formData.lastName || ''}`.trim(),
              email: formData.email || '',
              phone: formData.phoneNumber || ''
            }));
          } catch (e) {
            console.error('Error parsing form data:', e);
          }
        }
      }
      
      // Fetch availability from Zapier via backend
      await fetchAvailability(storedSubmissionId);
    };
    
    init();
  }, []);

  const fetchAvailability = async (subId) => {
    setLoading(true);
    try {
      // First check if we have agent assignment with availability
      if (subId) {
        const response = await fetch(`${BACKEND_URL}/api/agent-assignment/${subId}`);
        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setAgentInfo({
            name: data.data.agent_name,
            email: data.data.agent_email,
            phone: data.data.agent_phone
          });
          
          if (data.data.availability && data.data.availability.length > 0) {
            setAvailability(data.data.availability);
            setLoading(false);
            return;
          }
        }
      }
      
      // If no availability from agent assignment, fetch from Zapier
      const availResponse = await fetch(`${BACKEND_URL}/api/fetch-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submission_id: subId || 'new_request' })
      });
      
      const availData = await availResponse.json();
      
      if (availData.status === 'success' && availData.availability && availData.availability.length > 0) {
        setAvailability(availData.availability);
        if (availData.agent) {
          setAgentInfo(availData.agent);
        }
      } else {
        // Generate default availability for next 14 days if none available
        generateDefaultAvailability();
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
      // Generate default availability as fallback
      generateDefaultAvailability();
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultAvailability = () => {
    // Generate availability for next 14 business days
    // Monday-Thursday: 9AM-5PM, Friday: 9AM-4PM
    const slots = [];
    let daysAdded = 0;
    let currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset to midnight
    currentDate.setDate(currentDate.getDate() + 1); // Start from tomorrow
    
    while (daysAdded < 14) {
      const dayOfWeek = currentDate.getDay();
      
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        const endHour = dayOfWeek === 5 ? 16 : 17; // Friday ends at 4PM, others at 5PM
        
        // Generate hourly slots on the hour
        for (let hour = 9; hour < endHour; hour++) {
          let timeStr;
          if (hour < 12) {
            timeStr = `${hour}:00 AM`;
          } else if (hour === 12) {
            timeStr = '12:00 PM';
          } else {
            timeStr = `${hour - 12}:00 PM`;
          }
          
          slots.push({
            date: dateStr,
            time: timeStr,
            duration: 60
          });
        }
        daysAdded++;
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    setAvailability(slots);
  };

  const groupSlotsByDate = () => {
    const grouped = {};
    availability.forEach(slot => {
      if (!grouped[slot.date]) {
        grouped[slot.date] = [];
      }
      grouped[slot.date].push(slot);
    });
    return grouped;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleBooking = async () => {
    if (!selectedSlot || !meetingType) {
      toast.error('Please select a meeting type and time slot');
      return;
    }
    
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    
    try {
      const bookingData = {
        submission_id: submissionId || `booking_${Date.now()}`,
        meeting_type: meetingType,
        selected_date: selectedSlot.date,
        selected_time: selectedSlot.time,
        duration: selectedSlot.duration || 60,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        additional_notes: customerInfo.notes,
        agent_name: agentInfo?.name || '',
        agent_email: agentInfo?.email || ''
      };

      // Send to backend which will forward to Zapier
      const response = await fetch(`${BACKEND_URL}/api/schedule-meeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setConfirmationData({
          ...bookingData,
          confirmationNumber: result.confirmation_id || `APM-${Date.now()}`
        });
        setStep(4);
        toast.success('Meeting scheduled successfully!');
      } else {
        throw new Error(result.message || 'Failed to schedule meeting');
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to schedule meeting. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const meetingTypes = [
    { id: 'phone', label: 'Phone Call', icon: Phone, description: 'We\'ll call you at your number' },
    { id: 'video', label: 'Video Meeting', icon: Video, description: 'Meet via Zoom or Teams' },
    { id: 'in-person', label: 'In-Person', icon: MapPin, description: '383 W 37th St Ste 202, Loveland' }
  ];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#003087] mx-auto mb-4" />
            <p className="text-lg text-gray-700">Loading available times...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Confirmation state
  if (step === 4 && confirmationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#003087] mb-2">Meeting Scheduled!</h2>
            <p className="text-gray-600 mb-6">A calendar invite has been sent to your email.</p>
            
            <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
              <div className="space-y-3">
                <div className="flex items-center">
                  <Calendar className="h-5 w-5 text-[#003087] mr-3" />
                  <span className="font-medium">{formatDate(confirmationData.selected_date)}</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-[#003087] mr-3" />
                  <span>{confirmationData.selected_time} (1 hour)</span>
                </div>
                <div className="flex items-center">
                  {meetingType === 'phone' && <Phone className="h-5 w-5 text-[#003087] mr-3" />}
                  {meetingType === 'video' && <Video className="h-5 w-5 text-[#003087] mr-3" />}
                  {meetingType === 'in-person' && <MapPin className="h-5 w-5 text-[#003087] mr-3" />}
                  <span className="capitalize">{confirmationData.meeting_type.replace('-', ' ')} Meeting</span>
                </div>
                {agentInfo?.name && (
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-[#003087] mr-3" />
                    <span>With {agentInfo.name}</span>
                  </div>
                )}
              </div>
            </div>

            {meetingType === 'in-person' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-blue-800">
                  <strong>Office Location:</strong><br />
                  383 W 37th St Ste 202<br />
                  Loveland, CO 80538
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => window.location.href = '/'}
                className="bg-[#003087] hover:bg-[#003087]/90 text-white"
              >
                Back to Home
              </Button>
              <Button
                onClick={() => window.location.href = '/advanced-quote'}
                variant="outline"
                className="border-[#003087] text-[#003087]"
              >
                Provide More Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const groupedSlots = groupSlotsByDate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/thank-you'}
            className="text-white hover:bg-white/20 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-white">Schedule a Meeting</h1>
          <p className="text-white/80 mt-2">Choose how and when you'd like to meet with us</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            <div className={`flex items-center ${step >= 1 ? 'text-white' : 'text-white/50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-white text-[#003087]' : 'bg-white/30'}`}>1</div>
              <span className="ml-2 hidden sm:inline">Meeting Type</span>
            </div>
            <div className="w-8 h-0.5 bg-white/30" />
            <div className={`flex items-center ${step >= 2 ? 'text-white' : 'text-white/50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-white text-[#003087]' : 'bg-white/30'}`}>2</div>
              <span className="ml-2 hidden sm:inline">Select Time</span>
            </div>
            <div className="w-8 h-0.5 bg-white/30" />
            <div className={`flex items-center ${step >= 3 ? 'text-white' : 'text-white/50'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-white text-[#003087]' : 'bg-white/30'}`}>3</div>
              <span className="ml-2 hidden sm:inline">Your Details</span>
            </div>
          </div>
        </div>

        {/* Step 1: Meeting Type Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-[#003087]">How would you like to meet?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {meetingTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setMeetingType(type.id);
                      setStep(2);
                    }}
                    className={`p-6 rounded-lg border-2 transition-all duration-200 text-left hover:border-[#003087] hover:shadow-lg ${
                      meetingType === type.id 
                        ? 'border-[#003087] bg-[#003087]/5' 
                        : 'border-gray-200'
                    }`}
                  >
                    <type.icon className="h-10 w-10 text-[#003087] mb-3" />
                    <h3 className="font-semibold text-[#003087] mb-1">{type.label}</h3>
                    <p className="text-sm text-gray-600">{type.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Time Selection */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#003087]">Select a Time</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(1)}
                  className="text-gray-600"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Change Type
                </Button>
              </div>
              <p className="text-gray-600">
                {meetingTypes.find(t => t.id === meetingType)?.label} - 1 hour duration
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
                {Object.entries(groupedSlots).map(([date, slots]) => (
                  <div key={date}>
                    <h3 className="font-semibold text-[#003087] mb-3 sticky top-0 bg-white py-2">
                      {formatDate(date)}
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {slots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedSlot(slot);
                            setStep(3);
                          }}
                          className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                            selectedSlot?.date === slot.date && selectedSlot?.time === slot.time
                              ? 'bg-[#003087] text-white border-[#003087]'
                              : 'border-gray-200 hover:border-[#003087] hover:bg-[#003087]/5'
                          }`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Customer Details */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#003087]">Your Details</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep(2)}
                  className="text-gray-600"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Change Time
                </Button>
              </div>
              <div className="bg-[#003087]/5 rounded-lg p-4 mt-4">
                <div className="flex items-center text-[#003087]">
                  <Calendar className="h-5 w-5 mr-2" />
                  <span className="font-medium">{formatDate(selectedSlot.date)}</span>
                  <span className="mx-2">•</span>
                  <Clock className="h-5 w-5 mr-2" />
                  <span>{selectedSlot.time}</span>
                  <span className="mx-2">•</span>
                  <span className="capitalize">{meetingType.replace('-', ' ')}</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Doe"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@example.com"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Calendar invite will be sent here</p>
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(970) 555-1234"
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any specific topics you'd like to discuss?"
                    className="mt-1"
                    rows={3}
                  />
                </div>

                {meetingType === 'in-person' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>Meeting Location:</strong><br />
                      AP Agency Office<br />
                      383 W 37th St Ste 202<br />
                      Loveland, CO 80538
                    </p>
                  </div>
                )}

                <Button
                  onClick={handleBooking}
                  disabled={submitting}
                  className="w-full bg-[#e01933] hover:bg-[#b30032] text-white py-3 mt-4"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    'Confirm Meeting'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Agent Info Card */}
        {agentInfo?.name && step < 4 && (
          <Card className="mt-4">
            <CardContent className="py-4">
              <div className="flex items-center">
                <User className="h-8 w-8 text-[#003087] mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Your Agent</p>
                  <p className="font-semibold text-[#003087]">{agentInfo.name}</p>
                </div>
                {agentInfo.phone && (
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[#003087]">{agentInfo.phone}</span>
                    {isOfficeHours() && (
                      <button
                        onClick={handleCallAgent}
                        className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-sm px-3 py-1 rounded-full transition-colors"
                        data-testid="call-agent-schedule-btn"
                      >
                        <PhoneCall className="h-4 w-4" />
                        Call Now
                      </button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Call Agent Modal */}
      {showCallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold text-[#003087] mb-4">
              Call {agentInfo?.name}
            </h3>
            <p className="text-gray-600 mb-4">
              Enter your phone number and we'll connect you directly to your agent.
            </p>
            <input
              type="tel"
              placeholder="(970) 555-1234"
              value={callbackPhone}
              onChange={(e) => setCallbackPhone(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#003087]"
              data-testid="schedule-callback-phone-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCallModal(false);
                  setCallbackPhone('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={initiateCallToAgent}
                disabled={callingAgent}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="schedule-initiate-call-btn"
              >
                {callingAgent ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Calling...
                  </>
                ) : (
                  <>
                    <PhoneCall className="h-4 w-4" />
                    Call Me
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleMeeting;
